import { createMessage, registerComponent } from "helpers";
import WYATTElement from "wyatt";

/**
 * @param {string} raw
 * @returns {string | null} 5 digits or null if invalid
 */
function normalizeAndValidateZip(raw) {
  const digits = raw.trim().replace(/\D/g, "");

  if (digits.length === 5) {
    return digits;
  }

  return null;
}

registerComponent(
  "delivery-options",
  class DeliveryOptions extends WYATTElement {
    constructor() {
      super();
      this.attachShadow({ mode: "open" });
      this.sku = null;
      this.zip = null;
      this.deliveryMethods = null;
      this.input = null;
      this.form = null;
      this.loading = false;
    }

    connectedCallback() {
      // initialize from attributes if present
      this.sku = this.getAttribute("sku") || null;

      this.subscribe(
        WYATT.PubSub.events.userLocationSet,
        this.handleLocationChange.bind(this),
      );

      if (!this.zip) {
        this.render();
      }
    }

    handleLocationChange() {
      const zip = WYATT.Geolocation?.location?.zip || null;
      if (zip === this.zip) return;
      this.zip = zip;

      if (this.sku && this.zip) {
        this.fetchDeliveryMethods();
      } else {
        this.render();
      }
    }

    async handleSubmit(event) {
      event.preventDefault();
      if (!this.input.value) return;
      const zip = normalizeAndValidateZip(this.input.value);
      if (!zip) {
        this.input.classList.add("text-box--invalid");
        this.input.setAttribute("aria-invalid", "true");
        createMessage(WYATT.t.locationSearchForm.invalidZip, { type: "error" });
        this.input.focus();
        return;
      }
      this.input.classList.remove("text-box--invalid");
      this.input.setAttribute("aria-invalid", "false");
      // setLocation publishes userLocationSet, which triggers the fetch
      await WYATT.Geolocation.updateLocationFromZip(zip);
    }

    renderDate(slot, date) {
      const [slotDom] = slot.assignedNodes();
      const dateSelector = slotDom.querySelector("[data-date]");
      if (dateSelector) {
        dateSelector.innerHTML = date;
      }
    }

    renderBackorder() {
      this.shadowRoot.innerHTML = `
        <slot></slot>
        <slot name="no-results"></slot>
      `;
    }

    renderDeliveryMethods() {
      const deliveryValues = Object.values(this.deliveryMethods).filter(
        Boolean,
      );
      if (!deliveryValues.length) {
        this.shadowRoot.innerHTML = `
          <slot></slot>
          <slot name="no-results"></slot>
        `;
        return;
      }

      let options = ``;
      if (this.deliveryMethods.premier_delivery) {
        options += `<slot name="primier-delivery"></slot>`;
      }
      if (this.deliveryMethods.carton_delivery) {
        options += `<slot name="carton-delivery"></slot>`;
      }

      if (this.deliveryMethods.pickup_delivery) {
        options += `<slot name="pickup-delivery"></slot>`;
      }

      this.shadowRoot.innerHTML = `
        <slot></slot>
        <div>${options}</div>
      `;

      // Render Premier Date
      if (this.deliveryMethods.premier_delivery) {
        const premierDeliverySlot = this.shadowRoot.querySelector(
          'slot[name="primier-delivery"]',
        );
        this.renderDate(
          premierDeliverySlot,
          this.deliveryMethods.premier_delivery,
        );
      }
      // Render Carton Date
      if (this.deliveryMethods.carton_delivery) {
        const cartonDeliverySlot = this.shadowRoot.querySelector(
          'slot[name="carton-delivery"]',
        );
        this.renderDate(
          cartonDeliverySlot,
          this.deliveryMethods.carton_delivery,
        );
      }
      // Render Pick-up Date
      if (this.deliveryMethods.pickup_delivery) {
        const pickupDelivery = this.shadowRoot.querySelector(
          'slot[name="pickup-delivery"]',
        );
        this.renderDate(pickupDelivery, this.deliveryMethods.pickup_delivery);
      }
    }

    renderZipForm() {
      this.shadowRoot.innerHTML = `
        <slot></slot>
        <slot name="zip-form"></slot>
      `;
      this.input = this.querySelector('input[name="zip"]');
      this.form = this.querySelector(".delivery-form");
      if (this.form) {
        this.form.addEventListener("submit", this.handleSubmit.bind(this));
      }
    }

    renderZipDisplay() {
      const zipDisplay = this.querySelector(".delivery-method__zip");
      if (!zipDisplay) return;
      const zipValue = zipDisplay.querySelector("[data-zip]");
      if (zipValue) {
        zipValue.textContent = this.zip || "";
      }
      zipDisplay.hidden = !this.zip;
    }

    render() {
      this.renderZipDisplay();

      if (!this.zip) {
        this.renderZipForm();
        return;
      }

      if (this.loading && !this.deliveryMethods) {
        this.shadowRoot.innerHTML = `
          <slot></slot>
          <slot name="loading"></slot>
        `;
        return;
      }

      if (this.deliveryMethods) {
        this.renderDeliveryMethods();
        return;
      }

      // zip present but no methods found
      this.shadowRoot.innerHTML = `
        <slot></slot>
        <slot name="error"></slot>
      `;
    }

    parseDate(date) {
      if (!date || date === "N/A") return null;
      const newDate = new Date(date);
      const options = { weekday: "long", month: "long", day: "numeric" };
      return newDate.toLocaleDateString("en-US", options);
    }

    parseDeliveryMethods(data) {
      const {
        PremierDeliveryDate: premierDeliverDate = null,
        InCartonDeliveryDate: cartonDeliveryDate = null,
        PickupDate: pickupDate = null,
        BeddingOnlyDate: beddingOnlyDate = null,
      } = data;

      return {
        premier_delivery: this.parseDate(premierDeliverDate),
        carton_delivery: this.parseDate(cartonDeliveryDate),
        pickup_delivery: this.parseDate(pickupDate),
        bedding_delivery: this.parseDate(beddingOnlyDate),
      };
    }

    async fetchDeliveryMethods() {
      if (!this.sku || !this.zip) return null;
      this.loading = true;
      this.deliveryMethods = null;
      this.render();

      try {
        const params = new URLSearchParams({
          productid: this.sku,
          zip: this.zip,
        });
        const url = `/apps/ethel/pdp/availability?${params}`;
        const response = await fetch(url);

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();

        this.deliveryMethods = this.parseDeliveryMethods(data) || null;
        this.loading = false;

        this.render();

        return this.deliveryMethods;
      } catch (e) {
        window.console.error("Error Fetching Delivery Methods", e);
        this.loading = false;
        this.deliveryMethods = null;
        this.render();
        return null;
      }
    }
  },
);
